import { createFileRoute } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { MapPin, ShieldCheck, Sprout, CalendarDays } from 'lucide-react';

import { PRACTICE_LABELS } from '@sreerasa/shared';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useFarms } from '@/lib/queries';
import { farmPhotoUrl } from '@/lib/supabase';
import { Reveal, transitions } from '@/components/motion/primitives';

export const Route = createFileRoute('/farms')({
  component: FarmsPage,
});

function FarmsPage() {
  const { data: farms, isLoading } = useFarms();

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Reveal>
        <h1 className="font-display text-display font-black text-soil-900">The farms</h1>
        <p className="mt-3 max-w-2xl text-lg text-soil-600">
          Every farm here has been visited and verified. You get the village, the district, and the
          farmer's name — enough to know exactly where your food came from.
        </p>
      </Reveal>

      {isLoading ? (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }, (_, i) => (
            <Skeleton key={i} className="h-80 rounded-card" />
          ))}
        </div>
      ) : farms && farms.length > 0 ? (
        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {farms.map((farm, index) => (
            <motion.article
              key={farm.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ ...transitions.base, delay: index * 0.06 }}
              whileHover={{ y: -6 }}
              className="overflow-hidden rounded-card border border-soil-200 bg-white shadow-soft transition-shadow hover:shadow-lift"
            >
              <div className="relative aspect-video bg-gradient-to-br from-field-200 to-harvest-100">
                {farmPhotoUrl(farm.photo_url) ? (
                  <img
                    src={farmPhotoUrl(farm.photo_url)!}
                    alt=""
                    className="size-full object-cover"
                  />
                ) : (
                  <div className="flex size-full items-center justify-center">
                    <Sprout className="size-12 text-farm-600/25" aria-hidden />
                  </div>
                )}
                <Badge variant="success" size="sm" className="absolute right-3 top-3 shadow-soft">
                  <ShieldCheck aria-hidden /> Verified
                </Badge>
              </div>

              <div className="p-5">
                <h2 className="font-display text-lg font-bold text-soil-900">{farm.name}</h2>

                <p className="mt-1 flex items-center gap-1.5 text-sm text-farm-700">
                  <MapPin className="size-3.5 shrink-0" aria-hidden />
                  {[farm.village, farm.mandal, farm.district].filter(Boolean).join(', ')}
                </p>

                <p className="mt-2 text-sm text-soil-600">
                  Farmed by{' '}
                  <span className="font-semibold text-soil-900">{farm.farmer_display_name}</span>
                </p>

                {farm.established_year && (
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-soil-500">
                    <CalendarDays className="size-3" aria-hidden />
                    Since {farm.established_year}
                  </p>
                )}

                {farm.story && (
                  <p className="mt-3 line-clamp-4 text-sm leading-relaxed text-soil-600">
                    {farm.story}
                  </p>
                )}

                {farm.practices && farm.practices.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {farm.practices.map((practice: string) => (
                      <Badge key={practice} variant="farm" size="sm">
                        {PRACTICE_LABELS[practice] ?? practice.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      ) : (
        <div className="mt-10 rounded-card border border-dashed border-soil-300 bg-white/50 p-16 text-center">
          <Sprout className="mx-auto size-9 text-soil-300" aria-hidden />
          <h2 className="mt-4 font-display text-xl font-bold text-soil-800">
            No verified farms yet
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-soil-500">
            We're visiting the first farms now. A farm only appears here once someone has actually
            been there — which is slower, and the entire point.
          </p>
        </div>
      )}
    </div>
  );
}
